import axios from 'axios';
import { useEffect, useState } from 'react';

export type DspChannelMap = Record<string, string[]>;

interface DspChannelMapResponse {
  data: DspChannelMap;
}

// Reference data (flank#4627), but whitelabel-aware since flank#4469: a
// renamed/hidden dsp or channel only resolves correctly once the backend
// knows which single whitelabel is in scope (see ribeye-api's
// resolveReportingWhitelabelConfig — an unscoped admin view can't safely
// guess one). Cached per whitelabel-scope key, not once globally — a flat
// one-time cache was the actual bug Martin's filter-bar retest hit: the
// first whitelabel picked in a session would stick for the rest of it.
const cache = new Map<string, DspChannelMap>();
const inflight = new Map<string, Promise<DspChannelMap>>();

const scopeKey = (whitelabelIds: string[]): string => JSON.stringify(whitelabelIds);

const fetchDspChannelMap = async (whitelabelIds: string[]): Promise<DspChannelMap> => {
  const key = scopeKey(whitelabelIds);
  const hit = cache.get(key);
  if (hit) return hit;
  let pending = inflight.get(key);
  if (!pending) {
    pending = axios
      .get<DspChannelMapResponse>('/reporting-2.0/dsp-channel-map', {
        params: whitelabelIds.length ? { whitelabels: JSON.stringify(whitelabelIds) } : {},
      })
      .then(response => {
        const result = response.data?.data ?? {};
        cache.set(key, result);
        return result;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, pending);
  }
  return pending;
};

/**
 * Which channels each DSP can run, and under what name, for this whitelabel
 * scope — used to narrow the Channel filter chips to what the selected
 * Platform(s) actually support (flank#4627), with renames/hides applied
 * (flank#4469).
 *
 * `error` is surfaced separately from `map` on purpose: an empty map is not
 * "no restriction", it's "these platforms support zero channels" — treating
 * a failed fetch as an empty map would disable every Channel chip the moment
 * a Platform is selected (CodeRabbit, flank#4641). Callers should treat
 * `loading || error` as "don't restrict yet", same as before this map ever
 * loaded.
 *
 * `enabled` defaults to true for back-compat, but every caller should pass
 * `canViewPlatform` (flank#4741 QA): the full DSP list this map responds with
 * is a real data exposure for a user without Platform visibility, visible in
 * the network tab even though nothing renders it on screen. Skipping the
 * fetch entirely is the only way the frontend can close that — the response
 * itself still comes from ribeye-api unfiltered either way.
 */
const useDspChannelMap = (
  enabled = true,
  whitelabelIds: string[] = [],
): { map: DspChannelMap; loading: boolean; error: boolean } => {
  const key = scopeKey(whitelabelIds);
  const [map, setMap] = useState<DspChannelMap>(cache.get(key) ?? {});
  const [loading, setLoading] = useState(enabled && !cache.has(key));
  const [error, setError] = useState(false);

  useEffect(() => {
    // Re-runs on `enabled` flipping true (permissions resolving late) and on
    // the whitelabel scope changing (picking a different White Label filter
    // value) — without both in the deps this would only ever check once, at
    // mount, and keep showing the first whitelabel's map forever.
    //
    // Clears a stale error from a PREVIOUS scope's failed fetch (CodeRabbit,
    // flank#4915) — nothing else ever reset it, so a failure on whitelabel A
    // left `dspChannelMapUnresolved` (loading || error) stuck true after
    // switching to whitelabel B, even once B's fetch succeeded.
    setError(false);
    if (!enabled) {
      setLoading(false);
      return;
    }
    const cached = cache.get(key);
    if (cached) {
      setMap(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchDspChannelMap(whitelabelIds)
      .then(result => {
        if (!cancelled) setMap(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the stable, deep-equal form of `whitelabelIds`
  }, [enabled, key]);

  return { map, loading, error };
};

export default useDspChannelMap;
